const translations = {
  EN: {
    title: 'Chatty',
    localMfaDemo: 'Local MFA demo',
    login: {
      passwordRequirements: 'Use at least 8 characters, including an uppercase letter, a lowercase letter, and a number.',
      switchToLight: 'Switch to light mode',
      switchToDark: 'Switch to dark mode',
      setupFailed: 'Could not start authenticator setup. Please log in again.',
      invalidCredentials: 'Incorrect email address or password',
      passwordRequired: 'Enter your password',
      loginFailed: 'Login failed',
      currentPasswordRequired: 'Enter your current password',
      passwordsDoNotMatch: 'Passwords do not match',
      newPasswordMustDiffer: 'Choose a password different from your current password',
      passwordChanged: 'Password changed. Log in with your new password.',
      incorrectCurrentPassword: 'Your current password is incorrect',
      passwordPreviouslyUsed: 'You cannot reuse a previous password',
      invalidNewPassword: 'The new password does not meet the password requirements',
      newPasswordFailed: 'Could not set the new password',
      newPasswordSessionExpired: 'Your sign-in session expired. Please sign in again with your temporary password.',
      invalidCode: 'Could not verify the code. Try a new code.',
      demoNotice: 'Local MFA demo · No Cognito connection; API requests use 127.0.0.1 only',
      demoCredentials: 'Test account:',
      passwordChangeTitle: 'Password change required',
      setupTitle: 'Set up an authenticator app',
      codeTitle: 'Enter verification code',
      welcomeTitle: 'Welcome to Chatty',
      passwordExpiredLine1: 'Your password has expired.',
      passwordExpiredLine2: 'Set a new password to continue.',
      newPasswordPrompt: 'Set a new password to continue',
      setupPrompt: 'Add the setup key to your authenticator app, then enter its six-digit code.',
      codePrompt: 'Enter the six-digit code shown in your authenticator app.',
      loginPrompt: 'Log in to continue',
      scanQr: 'Scan the QR code with your authenticator app or enter the setup key manually.',
      openAuthenticator: 'Open in authenticator app',
      keepSecretPrivate: 'Do not share this setup key.',
      qrLabel: 'Authenticator setup QR code',
      codeLabel: 'Verification code',
      codePlaceholder: 'Six-digit verification code',
      verifying: 'Verifying...',
      verify: 'Verify',
      backToLogin: 'Back to login',
      lostAuthenticator: 'If you cannot use your authenticator app, contact an administrator.',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      showNewPassword: 'Show new password',
      hideNewPassword: 'Hide new password',
      showConfirmPassword: 'Show confirmation password',
      hideConfirmPassword: 'Hide confirmation password',
      minLength: 'At least 8 characters',
      uppercase: 'At least one uppercase letter',
      lowercase: 'At least one lowercase letter',
      number: 'At least one number',
      changing: 'Changing...',
      changePassword: 'Change password',
      loggingIn: 'Logging in...',
      logIn: 'Log in',
      email: 'Email address',
      password: 'Password',
    },
    subtitle: 'product by OST',
    logout: 'Logout',
    admin: 'Admin Dashboard',
    welcome: "Hi there!",
    welcome2: "I'm here to help with company rules and operations.",
    placeholder: 'Ask Chatty anything...',
    newChat: 'New Chat',
    serviceUnavailable: 'Chatty is temporarily unavailable. Please try again later or contact the administrator.',
    chatHistory: '',
    noHistory: '',
    sendMessage: '',
    menu: 'Menu',
    language: 'Language',
    analyzing: 'Analyzing...',
    searching: 'Searching documents...',
    generating: 'Generating answer...',

    // Admin dashboard
    adminTitle: 'Chatty Admin',
    adminSubtitle: 'Admin Dashboard',
    goToChat: 'Go to Chat',
    tabAnalytics: 'Analytics',
    tabUsage: 'Usage',
    tabQuestions: 'Popular Questions',
    tabFeedback: 'User Feedback',
    tabDocuments: 'Documents',
    tabUsers: 'Users',
    userManagementDesc: 'Change account permissions between user and admin.',
    totalUsers: 'Total Users',
    adminUsersCount: 'Admins',
    regularUsersCount: 'Users',
    userSearchPlaceholder: 'Search users...',
    userName: 'User',
    roleLabel: 'Role',
    currentRole: 'Current Role',
    changeRole: 'Change Role',
    roleOwner: 'Owner',
    roleAdmin: 'Admin',
    roleHr: 'HR',
    roleUser: 'User',
    cannotManageAccount: 'You do not have permission to change this account.',
    updating: 'Updating...',
    creating: 'Creating...',
    addUser: 'Add User',
    addUserModalTitle: 'Add New User',
    actions: 'Actions',
    userStatus: 'Status',
    userStatusPending: 'Pending',
    userStatusConfirmed: 'Confirmed',
    deleteUser: 'Delete',
    deleteUserConfirm: (username) => `Are you sure you want to delete ${username}?`,
    cannotDemoteSelf: 'You cannot demote your own admin account.',
    cannotDeleteSelf: 'You cannot delete your own admin account.',
    cannotChangeOwnRole: 'You cannot change your own role.',
    currentUserTag: 'Me',
    noUsersYet: 'No users found.',
    createUserValidation: 'Email is required.',
    createUserInvalidEmail: 'Enter a valid email address.',
    createUserInvalidDomain: 'Use your @opensesame-tech.com company email address.',
    createUserFailed: 'Failed to create user.',
    deleteUserFailed: 'Failed to delete user.',
    bulkUserLimitExceeded: 'You can add up to 50 users at a time.',
    bulkUserDuplicateEmail: 'Duplicate email addresses were found.',
    bulkUserAlreadyExists: 'This email address is already registered.',
    bulkAddTab: 'Bulk Add',
    singleAddTab: 'Add One User',
    bulkEmailLabel: 'Email Addresses (Max. 50)',
    bulkEmailHint: 'Enter one email address per line.',
    bulkRoleHint: 'All users added in bulk will have the "User" role.',
    bulkUserCreateFailed: 'Failed to create user.',
    bulkCreating: 'Creating...',
    bulkUserResult: 'Registration Results',
    bulkUserSuccess: 'Success',
    bulkUserFailure: 'Failed',

    // Documents tab
    documentSearchPlaceholder: 'Search documents...',
    noDocumentsFound: 'No documents found.',
    documentName: 'Name',
    documentSize: 'Size',
    documentUploaded: 'Uploaded',
    documentActions: 'Actions',
    preview: 'Preview',
    download: 'Download',
    openInNewTab: 'Open in new tab',
    closePreview: 'Close preview',
    pdfViewerUnsupported: 'Your browser does not support inline PDF viewing.',
    downloadPdfLink: 'Click here to download the PDF',
    noPreviewContent: 'No preview content available for this source.',
    loadingPdf: 'Loading PDF...',
    pdfLoadFailed: 'Could not display this PDF.',
    findingPassage: 'Finding the cited passage...',
    citedPassage: 'Cited passage',
    passageNotHighlighted: 'Could not highlight the cited passage in the PDF. The answer was based on:',

    // Analytics tab
    totalQueries: 'Total Queries',
    uniqueUsers: 'Unique Users',
    avgConfidence: 'Avg Confidence',
    avgResponseTime: 'Avg Response Time',
    noAnalyticsData: 'No Analytics Data Yet',
    noAnalyticsDesc: 'Analytics data will appear here once the backend is connected.',

    // Usage tab
    noUsageData: 'No Usage Data Yet',
    noUsageDesc: 'Usage data will appear here once the backend is connected.',
    dailyQueryVolume: 'Daily Query Volume',
    noDailyData: 'No daily usage data yet.',
    queries: 'Queries',
    userUsage: 'Usage by User',
    noUserUsage: 'No user usage data for this period.',
    user: 'User',
    apiCalls: 'API Calls',
    tokens: 'Tokens',

    // Questions tab
    totalQuestions: 'Total Questions',
    answeredToday: 'Answered Today',
    pendingReview: 'Pending Review',
    documents: 'Documents',
    noQuestionsYet: 'No questions yet. Data will appear when admin API endpoints are connected.',
    upload: 'Upload',

    // Intent chart
    intentDistribution: 'Intent Distribution',
    noIntentData: 'No intent data recorded yet.',
    intentCompanyQuery: 'Company Query',
    intentGreeting: 'Greeting',
    intentSmallTalk: 'Small Talk',
    intentClarification: 'Clarification',
    intentOffTopic: 'Off Topic',

    // Language chart
    languageDistribution: 'Language Distribution',
    noLanguageData: 'No language data recorded yet.',

    // Topic categories
    topicCategories: 'Topic Categories',
    noTopicData: 'No topic data yet.',
    categorizedQueries: 'categorized queries',
    topicHrLeave: 'HR & Leave',
    topicBenefits: 'Benefits & Compensation',
    topicWorkplace: 'Workplace Policies',
    topicAdmin: 'Admin Procedures',
    topicCompanyInfo: 'Company Info',
    topicCareer: 'Career Development',
    topicFacilities: 'Facilities',
    topicItSystems: 'IT & Systems',
    topicOther: 'Other',
    confidence: 'confidence',
    noSourcesLabel: 'no sources',

    // Popular questions
    popularQuestions: 'Popular Questions',
    noPopularData: 'No popular questions data yet.',
    question: 'Question',
    count: 'Count',
    topic: 'Topic',

    // Date picker
    dateTo: 'to',
    startDate: 'Start Date',
    endDate: 'End Date',
    selectStartDate: 'Select Start Date',
    selectEndDate: 'Select End Date',
    selectDate: 'Select Date',

    // Feedback
    feedbackTitle: 'User Feedback',
    totalFeedback: 'Total Feedback',
    positiveFeedback: 'Positive',
    negativeFeedback: 'Negative',
    satisfactionRate: 'Satisfaction Rate',
    recentNegative: 'Recent Negative Feedback',
    recentPositive: 'Recent Positive Feedback',
    noFeedbackData: 'No feedback data yet.',

    // Disclaimer
    disclaimer: 'Chatty can make mistakes. Please verify important information.',

    // Usage info & profile info
    usageInfo: 'Usage Info',
    profileTitle: 'Profile Info',
    displayNameLabel: 'Display Name',
    emailLabel: 'Email Address',
    close: 'Close',
    save: 'Save',
    cancel: 'Cancel',
    previousPage: 'Previous',
    nextPage: 'Next',

    // Recent chats
    recentChats: 'Recent Chats',
    deleteChatTitle: 'Are you sure you want to delete this chat?',
    deleteChatTarget: 'This chat will be deleted.',
    deleteChat: 'Delete',
    chatExpiresSoon: 'This chat will be automatically deleted within 7 days',

    // Theme
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
  },
  '日本語': {
    title: 'Chatty',
    localMfaDemo: 'ローカルMFAデモ',
    login: {
      passwordRequirements: 'パスワードは8文字以上で、英大文字・英小文字・数字をそれぞれ1文字以上含めてください。',
      switchToLight: 'ライトモードに切り替え',
      switchToDark: 'ダークモードに切り替え',
      setupFailed: '認証アプリの設定を開始できませんでした。もう一度ログインしてください。',
      invalidCredentials: 'メールアドレスまたはパスワードが正しくありません',
      passwordRequired: 'パスワードを入力してください',
      loginFailed: 'ログインに失敗しました',
      currentPasswordRequired: '現在のパスワードを入力してください',
      passwordsDoNotMatch: 'パスワードが一致しません',
      newPasswordMustDiffer: '新しいパスワードは現在のパスワードと異なるものを設定してください',
      passwordChanged: 'パスワードを変更しました。新しいパスワードでログインしてください。',
      incorrectCurrentPassword: '現在のパスワードが正しくありません',
      passwordPreviouslyUsed: '過去に使用したパスワードは再利用できません',
      invalidNewPassword: '新しいパスワードがパスワードポリシーを満たしていません',
      newPasswordFailed: '新しいパスワードの設定に失敗しました',
      newPasswordSessionExpired: 'ログインセッションの有効期限が切れました。仮パスワードで再度ログインしてください',
      invalidCode: '認証コードを確認できませんでした。新しいコードでお試しください。',
      demoNotice: 'ローカルMFAデモ · Cognitoには接続せず、APIは127.0.0.1のみ',
      demoCredentials: 'テスト用:',
      passwordChangeTitle: 'パスワードの変更が必要です',
      setupTitle: '認証アプリを設定',
      codeTitle: '認証コードを入力',
      welcomeTitle: 'Chattyへようこそ',
      passwordExpiredLine1: 'パスワードの有効期限が切れましたので、',
      passwordExpiredLine2: '新しいパスワードを設定してください',
      newPasswordPrompt: '続行するには新しいパスワードを設定してください',
      setupPrompt: '認証アプリに設定キーを登録し、表示された6桁のコードを入力してください。',
      codePrompt: '認証アプリに表示された6桁のコードを入力してください。',
      loginPrompt: 'ログインしてください',
      scanQr: '認証アプリでQRコードを読み取るか、設定キーを手動で入力してください。',
      openAuthenticator: '認証アプリで開く',
      keepSecretPrivate: '設定キーは他の人と共有しないでください。',
      qrLabel: '認証アプリ設定QRコード',
      codeLabel: '認証コード',
      codePlaceholder: '6桁の認証コード',
      verifying: '確認中...',
      verify: '認証する',
      backToLogin: 'ログインに戻る',
      lostAuthenticator: '認証アプリを利用できない場合は、管理者に連絡してください。',
      currentPassword: '現在のパスワード',
      newPassword: '新しいパスワード',
      confirmPassword: '新しいパスワードを確認',
      showNewPassword: '新しいパスワードを表示',
      hideNewPassword: '新しいパスワードを隠す',
      showConfirmPassword: '確認用パスワードを表示',
      hideConfirmPassword: '確認用パスワードを隠す',
      minLength: '8文字以上',
      uppercase: '英大文字を1文字以上含む',
      lowercase: '英小文字を1文字以上含む',
      number: '数字を1文字以上含む',
      changing: '変更中...',
      changePassword: 'パスワードを変更',
      loggingIn: 'ログイン中...',
      logIn: 'ログイン',
      email: 'メールアドレス',
      password: 'パスワード',
    },
    subtitle: 'OSTの製品',
    logout: 'ログアウト',
    admin: '管理ダッシュボード',
    welcome: 'こんにちは！',
    welcome2: '社内のルールや業務に関する情報をご案内します!',
    placeholder: 'チャッティーに何でもお聞きください...',
    newChat: '新しいチャット',
    serviceUnavailable: 'Chattyは現在一時的にご利用いただけません。しばらくしてから再度お試しいただくか、管理者にお問い合わせください。',
    chatHistory: '',
    noHistory: '',
    sendMessage: '',
    menu: 'メニュー',
    language: '言語',
    analyzing: '分析中...',
    searching: 'ドキュメント検索中...',
    generating: '回答生成中...',

    // Admin dashboard
    adminTitle: 'Chatty 管理画面',
    adminSubtitle: '管理ダッシュボード',
    goToChat: 'チャットへ',
    tabAnalytics: '分析',
    tabUsage: '利用状況',
    tabQuestions: 'よくある質問',
    tabFeedback: 'ユーザーフィードバック',
    tabDocuments: 'ドキュメント',
    tabUsers: 'ユーザー',
    userManagementDesc: 'アカウント権限をユーザーと管理者の間で変更します。',
    totalUsers: '全',
    adminUsersCount: '管理者',
    regularUsersCount: '一般ユーザー',
    userSearchPlaceholder: 'ユーザーを検索...',
    userName: 'ユーザー',
    roleLabel: '権限',
    currentRole: '現在の権限',
    changeRole: '権限変更',
    roleOwner: 'オーナー',
    roleAdmin: '管理者',
    roleHr: '人事',
    roleUser: 'ユーザー',
    cannotManageAccount: 'このアカウントを変更する権限がありません。',
    updating: '更新中...',
    creating: '作成中...',
    addUser: 'ユーザー追加',
    addUserModalTitle: '新規ユーザー追加',
    actions: '操作',
    userStatus: 'ステータス',
    userStatusPending: '変更待ち',
    userStatusConfirmed: '確認済み',
    deleteUser: '削除',
    deleteUserConfirm: (username) => `${username} を削除してもよろしいですか？`,
    cannotDemoteSelf: '自分の管理者権限は解除できません。',
    cannotDeleteSelf: '自分の管理者アカウントは削除できません。',
    cannotChangeOwnRole: '自分のロールは変更できません。',
    currentUserTag: '自分',
    noUsersYet: 'ユーザーが見つかりません。',
    createUserValidation: 'メールアドレスは必須です。',
    createUserInvalidEmail: '有効なメールアドレスを入力してください。',
    createUserInvalidDomain: '@opensesame-tech.com の会社メールアドレスを使用してください。',
    createUserFailed: 'ユーザーの作成に失敗しました。',
    deleteUserFailed: 'ユーザーの削除に失敗しました。',
    bulkUserEmailRequired: 'メールアドレスを入力してください。',
    bulkUserLimitExceeded: '一度に追加できるユーザーは最大50人です。',
    bulkUserDuplicateEmail: '同じメールアドレスが重複しています。',
    bulkUserAlreadyExists: '既に登録済みのメールアドレスです。',
    bulkAddTab: '一括追加',
    singleAddTab: '1人ずつ追加',
    bulkEmailLabel: 'メールアドレス(最大50人)',
    bulkEmailHint: 'メールアドレスを1行に1件ずつ入力してください。',
    bulkRoleHint: '一括追加するユーザーの権限は「ユーザー」に固定されます。',
    bulkUserCreateFailed:'ユーザの登録に失敗しました。',
    bulkCreating: '登録中...',
    bulkUserResult: '登録結果',
    bulkUserSuccess: '成功',
    bulkUserFailure: '失敗',

    // Documents tab
    documentSearchPlaceholder: 'ドキュメントを検索...',
    noDocumentsFound: 'ドキュメントが見つかりません。',
    documentName: '名前',
    documentSize: 'サイズ',
    documentUploaded: 'アップロード日時',
    documentActions: '操作',
    preview: 'プレビュー',
    download: 'ダウンロード',
    openInNewTab: '新しいタブで開く',
    closePreview: 'プレビューを閉じる',
    pdfViewerUnsupported: 'お使いのブラウザーはPDFのインライン表示に対応していません。',
    downloadPdfLink: 'PDFをダウンロードするにはこちらをクリックしてください',
    noPreviewContent: 'このソースで利用できるプレビューコンテンツはありません。',
    loadingPdf: 'PDFを読み込んでいます...',
    pdfLoadFailed: 'このPDFを表示できませんでした。',
    findingPassage: '引用箇所を探しています...',
    citedPassage: '引用箇所',
    passageNotHighlighted: 'PDF内で引用箇所をハイライトできませんでした。回答の根拠は次の部分です:',

    // Analytics tab
    totalQueries: '総クエリ数',
    uniqueUsers: 'ユニークユーザー',
    avgConfidence: '平均信頼度',
    avgResponseTime: '平均応答時間',
    noAnalyticsData: '分析データがありません',
    noAnalyticsDesc: 'バックエンドに接続されると、分析データがここに表示されます。',

    // Usage tab
    noUsageData: '利用データがありません',
    noUsageDesc: 'バックエンドに接続されると、利用データがここに表示されます。',
    dailyQueryVolume: '日別クエリ数',
    noDailyData: '日別利用データがありません。',
    queries: 'クエリ',
    userUsage: 'ユーザー別利用状況',
    noUserUsage: 'この期間のユーザー別利用データはありません。',
    user: 'ユーザー',
    apiCalls: 'APIコール数',
    tokens: 'トークン数',

    // Questions tab
    totalQuestions: '総質問数',
    answeredToday: '今日の回答',
    pendingReview: 'レビュー待ち',
    documents: 'ドキュメント',
    noQuestionsYet: 'まだ質問がありません。管理APIエンドポイントが接続されるとデータが表示されます。',
    upload: 'アップロード',

    // Intent chart
    intentDistribution: 'インテント分布',
    noIntentData: 'インテントデータがまだありません。',
    intentCompanyQuery: '会社関連の質問',
    intentGreeting: '挨拶',
    intentSmallTalk: '雑談',
    intentClarification: '確認',
    intentOffTopic: 'トピック外',

    // Language chart
    languageDistribution: '言語分布',
    noLanguageData: '言語データがまだありません。',

    // Topic categories
    topicCategories: 'トピックカテゴリ',
    noTopicData: 'トピックデータがありません。',
    categorizedQueries: '件のカテゴリ分類済みクエリ',
    topicHrLeave: '人事・休暇',
    topicBenefits: '福利厚生・報酬',
    topicWorkplace: '職場ポリシー',
    topicAdmin: '事務手続き',
    topicCompanyInfo: '会社情報',
    topicCareer: 'キャリア',
    topicFacilities: '設備',
    topicItSystems: 'IT・システム',
    topicOther: 'その他',
    confidence: '信頼度',
    noSourcesLabel: 'ソースなし',

    // Popular questions
    popularQuestions: 'よくある質問',
    noPopularData: 'よくある質問のデータがありません。',
    question: '質問',
    count: '回数',
    topic: 'トピック',

    // Date picker
    dateTo: 'から',
    startDate: '開始日',
    endDate: '終了日',
    selectStartDate: '開始日を選択',
    selectEndDate: '終了日を選択',
    selectDate: '日付を選択',

    // Feedback
    feedbackTitle: 'ユーザーフィードバック',
    totalFeedback: 'フィードバック合計',
    positiveFeedback: 'ポジティブ',
    negativeFeedback: 'ネガティブ',
    satisfactionRate: '満足度',
    recentNegative: '最近のネガティブフィードバック',
    recentPositive: '最近のポジティブフィードバック',
    noFeedbackData: 'フィードバックデータがまだありません。',

    // Disclaimer
    disclaimer: 'Chattyの回答には誤りがある場合があります。重要な情報がご確認してください。',

    // Usage info & profile info
    usageInfo: '利用状況',
    profileTitle: 'プロフィール情報',
    displayNameLabel: '表示名',
    emailLabel: 'メールアドレス',
    close: '閉じる',
    save: '保存',
    cancel: 'キャンセル',
    previousPage: '前へ',
    nextPage: '次へ',

    // Recent chats
    recentChats: '最近のチャット',
    deleteChatTitle: 'このチャットを削除してもよろしいでしょうか？',
    deleteChatTarget: '以下が削除されます。',
    deleteChat: '削除',
    chatExpiresSoon: 'このチャットは7日以内に自動的に削除されます。',

    // Theme
    lightMode: 'ライトモード',
    darkMode: 'ダークモード',
  },
};

export default translations;
